import type { CSSProperties } from 'react'

export type DashboardReskinTileState = 'launch' | 'locked' | 'replay'

export const DASHBOARD_RESKIN_TILE_SIZE_CLASS = 'aspect-[326/448] w-full'
export const DASHBOARD_RESKIN_TILE_FLEX_CLASS = 'min-w-0 flex-1 overflow-visible w-full'
export const DASHBOARD_RESKIN_TILE_IMAGE_CLASS = 'h-full w-full object-contain'
export const DASHBOARD_RESKIN_LAUNCH_TILE_IMAGE_CLASS = 'absolute left-1/2 top-1/2 w-[calc(100%*371/326)] max-w-none -translate-x-1/2 -translate-y-1/2'
export const DASHBOARD_RESKIN_WORDMARK_SIZE_CLASS = 'h-[133px] w-[361px]'
export const DASHBOARD_RESKIN_MENU_ICON_SIZE_CLASS = 'h-[122px] w-[204px]'

export const getDashboardReskinTileSizeClass = (
  _state: DashboardReskinTileState
): string => DASHBOARD_RESKIN_TILE_SIZE_CLASS

export const getDashboardReskinTileFlexClass = (
  _state: DashboardReskinTileState
): string => DASHBOARD_RESKIN_TILE_FLEX_CLASS

export const getDashboardReskinTileImageClass = (
  state: DashboardReskinTileState
): string => state === 'launch'
  ? DASHBOARD_RESKIN_LAUNCH_TILE_IMAGE_CLASS
  : DASHBOARD_RESKIN_TILE_IMAGE_CLASS

const DASHBOARD_RESKIN_DIR = '/assets/images/dashboard-reskin'

export const DASHBOARD_RESKIN_WORDMARK_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/ZenithShield Wordmark - SpeedGlow - 2tone.svg`
)
export const DASHBOARD_RESKIN_MENU_ICON_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Menu Icon - PlayerPortal.svg`
)
export const DASHBOARD_RESKIN_BACKGROUND_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Dashboard Background.jpg`
)
export const DASHBOARD_DEFAULT_BACKGROUND_IMAGE = 'radial-gradient(circle at top, #2C2C2C, #090909)'

export const getDashboardContainerBackgroundStyle = (
  useReskinChrome: boolean
): CSSProperties => useReskinChrome
  ? {
      backgroundImage: `url(${DASHBOARD_RESKIN_BACKGROUND_SRC})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: '#000000'
    }
  : {
      backgroundImage: DASHBOARD_DEFAULT_BACKGROUND_IMAGE
    }

interface DashboardReskinTileSet {
  launch?: string
  replay?: string
  locked?: Partial<Record<number, string>>
}

export const dashboardReskinTiles: Record<string, DashboardReskinTileSet> = {
  cocaine: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level1 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level1 - REPLAY.svg`
  },
  xanax: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level2 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level2 - REPLAY.svg`,
    locked: {
      1: `${DASHBOARD_RESKIN_DIR}/Level2 - LOCKED 1.svg`
    }
  },
  mdma: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level3 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level3 - REPLAY.svg`,
    locked: {
      1: `${DASHBOARD_RESKIN_DIR}/Level3 - LOCKED 1.svg`,
      2: `${DASHBOARD_RESKIN_DIR}/Level3 - LOCKED 2.svg`
    }
  },
  response: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level4 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level4 - REPLAY.svg`,
    locked: {
      1: `${DASHBOARD_RESKIN_DIR}/Level4 - LOCKED 1.svg`,
      2: `${DASHBOARD_RESKIN_DIR}/Level4 - LOCKED 2.svg`,
      3: `${DASHBOARD_RESKIN_DIR}/Level4 - LOCKED 3.svg`
    }
  }
}

export const getDashboardReskinTileState = (
  module: { isCurrent?: boolean, isLocked?: boolean },
  reviewState: boolean
): DashboardReskinTileState => {
  if (reviewState) {
    return 'replay'
  }
  if (module.isCurrent === true) {
    return 'launch'
  }
  if (module.isLocked === true) {
    return 'locked'
  }
  return 'replay'
}

export const getDashboardReskinLockedIteration = (currentModuleIndex: number): number => {
  if (currentModuleIndex < 0) {
    return 1
  }
  return currentModuleIndex + 1
}

export const getDashboardReskinTileSrc = (
  moduleName: string,
  state: DashboardReskinTileState,
  lockedIteration: number = 1
): string | undefined => {
  const tiles = dashboardReskinTiles[moduleName.toLowerCase()]
  if (tiles == null) {
    return undefined
  }

  const src = state === 'locked'
    ? tiles.locked?.[lockedIteration]
    : tiles[state]
  if (src == null) {
    return undefined
  }
  return encodeURI(src)
}
